import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Switch,
  IconButton,
  TextField,
  Button,
} from '@mui/material';
import {
  ArrowBack,
  DarkMode,
  Notifications,
  Language,
  Info,
  Fingerprint,
  Delete,
  Edit,
} from '@mui/icons-material';
import { startRegistration } from '@simplewebauthn/browser';
import { useThemeStore, useAuthStore } from '@/store';
import { useNotification } from '@/contexts/NotificationContext';
import {
  enablePushNotifications,
  getNotificationSettings,
  setNotificationSettings,
  supportsWebPush,
} from '@/services/pushNotifications';
import { authService } from '@/api/services';
import type { AuthWebAuthnCredential } from '@/types';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { mode, toggleMode } = useThemeStore();
  const { isAuthenticated, logout } = useAuthStore();
  const { showError, showSuccess } = useNotification();
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [passkeys, setPasskeys] = useState<AuthWebAuthnCredential[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const loadPasskeys = useCallback(async () => {
    if (!isAuthenticated) {
      setPasskeys([]);
      return;
    }
    try {
      setPasskeys(await authService.listPasskeys());
    } catch {
      setPasskeys([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPushEnabled(false);
      return;
    }
    let active = true;
    getNotificationSettings()
      .then(async (settings) => {
        const subscription = supportsWebPush()
          ? await (await navigator.serviceWorker.ready).pushManager.getSubscription()
          : null;
        if (active) {
          setPushEnabled(
            settings.pushEnabled && Notification.permission === 'granted' && Boolean(subscription),
          );
        }
      })
      .catch(() => {
        if (active) setPushEnabled(false);
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    void loadPasskeys();
  }, [loadPasskeys]);

  const handlePushChange = async (_: React.ChangeEvent<HTMLInputElement>, enabled: boolean) => {
    setPushLoading(true);
    try {
      if (enabled) {
        await enablePushNotifications();
      } else {
        await setNotificationSettings(false);
      }
      setPushEnabled(enabled);
      showSuccess(enabled ? 'Push-уведомления включены' : 'Push-уведомления выключены');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Не удалось изменить настройки уведомлений');
    } finally {
      setPushLoading(false);
    }
  };

  const handleAddPasskey = async () => {
    try {
      const begin = await authService.webauthnRegisterBegin();
      const attestation = await startRegistration({
        optionsJSON: begin.publicKey as unknown as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await authService.webauthnRegisterFinish({
        challengeId: begin.challengeId,
        credential: attestation as unknown as Record<string, unknown>,
      });
      showSuccess('Passkey сохранён');
      await loadPasskeys();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Не удалось добавить Passkey');
    }
  };

  const handleRename = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    try {
      await authService.renamePasskey(id, name);
      setEditingId(null);
      await loadPasskeys();
    } catch {
      showError('Не удалось переименовать');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await authService.deletePasskey(id);
      await loadPasskeys();
    } catch {
      showError('Не удалось удалить Passkey');
    }
  };

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [hash]);

  return (
    <Box sx={{ pb: 2 }}>
      <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 2 }}>
        <ArrowBack sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
        <Typography variant="h6" fontWeight="bold">Настройки</Typography>
      </Paper>

      <Paper sx={{ mx: 2, mb: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon><DarkMode /></ListItemIcon>
            <ListItemText primary="Темная тема" />
            <Switch checked={mode === 'dark'} onChange={toggleMode} />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><Notifications /></ListItemIcon>
            <ListItemText
              primary="Уведомления"
              secondary={!isAuthenticated ? 'Войдите, чтобы включить' : !supportsWebPush() ? 'Не поддерживаются браузером' : undefined}
            />
            <Switch
              checked={pushEnabled}
              disabled={!isAuthenticated || !supportsWebPush() || pushLoading}
              onChange={handlePushChange}
            />
          </ListItem>
        </List>
      </Paper>

      {isAuthenticated && (
        <>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
            Passkeys
          </Typography>
          <Paper sx={{ mx: 2, mb: 2 }}>
            <List>
              {passkeys.map((pk, index) => (
                <React.Fragment key={pk.id}>
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          edge="end"
                          aria-label="rename"
                          onClick={() => {
                            setEditingId(pk.id);
                            setEditName(pk.friendlyName);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(pk.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemIcon><Fingerprint /></ListItemIcon>
                    {editingId === pk.id ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, pr: 8 }}>
                        <TextField
                          size="small"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          fullWidth
                        />
                        <Button size="small" onClick={() => handleRename(pk.id)}>
                          OK
                        </Button>
                      </Box>
                    ) : (
                      <ListItemText
                        primary={pk.friendlyName}
                        secondary={pk.lastUsedAt ? `Использован: ${new Date(pk.lastUsedAt).toLocaleString()}` : 'Ещё не использовался'}
                      />
                    )}
                  </ListItem>
                  {index < passkeys.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {passkeys.length === 0 && (
                <ListItem>
                  <ListItemText primary="Нет сохранённых Passkey" secondary="Добавьте для быстрого входа" />
                </ListItem>
              )}
              <Divider />
              <ListItem disablePadding>
                <ListItemButton onClick={handleAddPasskey}>
                  <ListItemIcon><Fingerprint /></ListItemIcon>
                  <ListItemText primary="Добавить Passkey" />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>
        </>
      )}

      <Typography id="about" variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary', scrollMarginTop: 72 }}>
        О приложении
      </Typography>

      <Paper sx={{ mx: 2 }}>
        <List>
          <ListItem>
            <ListItemIcon><Info /></ListItemIcon>
            <ListItemText primary="Версия" secondary="1.0.0" />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><Language /></ListItemIcon>
            <ListItemText primary="Язык" secondary="Русский" />
          </ListItem>
        </List>
      </Paper>

      {isAuthenticated && (
        <Box sx={{ p: 2, mt: 2 }}>
          <ListItemButton onClick={logout} sx={{ color: 'error.main', justifyContent: 'center' }}>
            <Typography fontWeight="medium">Выйти</Typography>
          </ListItemButton>
        </Box>
      )}
    </Box>
  );
};
